import { useCallback } from 'react'
import { useGlobalEvent } from '@highlight/ipc/react'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { useToast } from '../use-toast'

type ToastApi = ReturnType<typeof useToast>

type DefaultModelMigratedPayload = {
  replacementId: string | null
  replacementLabel: string | null
}

/**
 * Notifies the user when their saved default model was deprecated and migrated.
 * The migration itself happens in the main process (ModelManager); this just
 * surfaces the result. Lives in the always-mounted overlay Toaster so the toast
 * shows regardless of which window triggered the model fetch.
 */
export function useModelMigrationToast(toast: ToastApi) {
  const handler = useCallback(
    (payload: DefaultModelMigratedPayload) => {
      // A truthy replacementId means the local store was updated to a specific
      // replacement; the label is just display polish (missing if the server-
      // resolved id isn't in the local catalog yet). Use it to pick the message,
      // but never let it switch the analytics action to "reset".
      if (payload?.replacementId) {
        toast.add({
          analytics: { type: 'default model migrated', hasReplacement: true },
          title: 'Default model updated',
          description: payload.replacementLabel
            ? `Your previous default model is no longer available, so we've switched you to ${payload.replacementLabel}. You can change it anytime in Settings.`
            : "Your previous default model is no longer available, so we've switched you to its replacement. You can change it anytime in Settings.",
        })
        AmplitudeRenderer.trackEvent('Settings - Models', {
          action: 'update-default-model',
          reason: 'default-model-deprecated',
          replacementModelId: payload.replacementId,
        })
      } else {
        toast.add({
          analytics: { type: 'default model migrated', hasReplacement: false },
          title: 'Default model updated',
          description:
            'Your previous default model is no longer available, so we switched you to the latest model. You can pick another in Settings.',
        })
        AmplitudeRenderer.trackEvent('Settings - Models', {
          action: 'reset-default-model',
          reason: 'default-model-deprecated',
        })
      }
    },
    [toast],
  )
  useGlobalEvent('default-model-migrated', handler)
}
